<template>
	<span
		ref="container"
		class="info-tooltip"
		:class="{ 'info-tooltip__primary': color === 'primary', 'info-tooltip__open': showTooltip }"
		@mouseleave="behavior.onContainerMouseLeave"
		@focusout="behavior.onContainerFocusOut"
		@mousedown="behavior.onContainerMouseDown"
		@mouseup="behavior.onContainerMouseUp"
		@click="behavior.onContainerClick($event)"
	>
		<button
			ref="icon"
			v-touch-target
			type="button"
			class="info-tooltip__button"
			aria-label="Mehr Informationen"
			:disabled="disabled"
			@mousedown="behavior.onButtonMouseDown"
			@mouseenter="behavior.onButtonMouseEnter"
			@focus="behavior.onButtonFocus"
			@click="behavior.onButtonClick"
		>
			<FaIcon
				:icon="['fa-regular', 'fa-circle-info']"
				class="info-tooltip__icon"
			/>
		</button>

		<div
			:id="props.config.elementId"
			ref="tooltip"
			role="tooltip"
			class="info-tooltip__tooltip"
			:data-show="showTooltip && tooltipReady && !disabled"
		>
			<slot />
			<div
				class="info-tooltip__arrow-wrapper"
				data-popper-arrow
			>
				<div class="info-tooltip__arrow"></div>
				<div class="info-tooltip__mouse-guide"></div>
			</div>
		</div>
	</span>
</template>

<script lang="ts" setup>
import { onMounted, ref, toRefs, watch, watchEffect } from 'vue';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import type { Instance } from '@popperjs/core';
import { createPopper } from '@popperjs/core';
import { vTouchTarget } from 'src/directives/TouchTarget/touchTarget';
import { isDevelopmentEnvironment } from '@utils/environmentUtils';
import { useTooltipBehavior } from '@lmt-rpb/InfoTooltip/tooltip-behavior';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { TooltipConfig, createTooltip } from '@lmt-rpb/InfoTooltip/tooltip-config';
import { useMediaQuery } from '@vueuse/core';

export interface Props {
	/** Generate using {@link createTooltip()}. */
	config: TooltipConfig;
	disabled?: boolean;
	color?: 'default' | 'primary';
	/* tooltip will switch between top and bottom if there is not enough space */
	placement?: 'top' | 'bottom';
}

const props = withDefaults(defineProps<Props>(), {
	disabled: false,
	color: 'default',
	placement: 'top',
});

const container = ref<HTMLElement>();
const icon = ref<HTMLElement>();
const tooltip = ref<HTMLElement>();
const showTooltip = ref(false);
const tooltipReady = ref(false);
let popper: Instance;

const behavior = useTooltipBehavior(showTooltip, container);
const hasCoarsePointer = useMediaQuery('(pointer: coarse)');
const propsRef = toRefs(props);

watchEffect(() => showTooltip.value && popper?.update());
watch([hasCoarsePointer, propsRef.placement], updatePlacement);
watch(() => props.config.elementId, checkAriaDescribedByExists, { flush: 'post' });

onMounted(async () => {
	setTimeout(() => {
		setupTooltip();
	}, 30);
});

function getCurrentTargetPlacement() {
	if (hasCoarsePointer.value) {
		return 'top';
	}
	return props.placement;
}

function updatePlacement() {
	popper?.setOptions({ placement: getCurrentTargetPlacement() });
}

function checkAriaDescribedByExists() {
	if (!isDevelopmentEnvironment()) {
		return;
	}

	const parentDescriber = icon.value?.closest(`[aria-describedby="${props.config.elementId}"]`);
	if (!parentDescriber) {
		console.error(
			`[InfoTooltip] No parent element has 'aria-describedby'-attribute set for this tooltip.
To fix this: use the exposed variable 'tooltipElementId' and set it on the element the tooltip belongs to logically.

Tooltip location:`,
			icon.value
		);
	}
}

function setupTooltip() {
	if (!icon.value || !tooltip.value) {
		return;
	}

	popper = createPopper(icon.value, tooltip.value, {
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 23],
				},
			},
			{
				name: 'preventOverflow',
				options: {
					boundary: document.querySelector('.list-item.is-verified'),
				},
			},
			{
				name: 'arrow',
				options: {
					padding: 10,
				},
			},
		],
		placement: getCurrentTargetPlacement(),
	});

	tooltipReady.value = true;
}
</script>

<style lang="scss" scoped>
$tooltip-color-text: $color-text;
$tooltip-color-background: $color-page-background;
$tooltip-color-icon: $color-text;
$tooltip-color-icon-primary: $color-primary;
$tooltip-color-border: $color-search-mask-border;
$tooltip-border-width: 1px;

@mixin arrow-direction($direction) {
	@if ($direction == 'down') {
		transform: translate(-50%, -12.5px) rotate(45deg);
	} @else {
		transform: translate(-50%, 7.5px) rotate(45deg);
	}
}

.info-tooltip {
	display: inline-block;
	line-height: 1;

	&__open .info-tooltip__button {
		position: relative;
		z-index: #{$tooltip-z + 1};

		&::after {
			display: block;
		}
	}

	&__button {
		position: relative;
		display: grid;
		background: none;
		border: 0;
		padding: 0;
		width: 1.6rem;
		height: 1.6rem;
		font-size: 1.6rem;
		line-height: 1;

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			display: none;
		}

		@include focus-visible-state(99px);
	}

	&__tooltip {
		position: absolute;
		z-index: $tooltip-z;
		isolation: isolate;
		visibility: hidden;
		opacity: 0;
		max-width: min(320px, 100vw);
		padding: 1.6rem;
		border: $tooltip-border-width solid $tooltip-color-border;
		border-radius: $border-radius-large;
		background: $tooltip-color-background;
		box-shadow: $box-shadow;
		color: $tooltip-color-text;
		font-size: 1.6rem;
		font-weight: normal;
		text-align: start;
		white-space: normal;
		line-height: normal;
		cursor: default;
		pointer-events: none;
		transition:
			visibility 0.2s,
			opacity 0.2s linear;

		&[data-show='true'] {
			visibility: visible;
			opacity: 1;
			pointer-events: auto;
		}

		&[data-popper-placement^='top'] .info-tooltip {
			&__arrow-wrapper {
				top: 100%;
			}

			&__mouse-guide {
				top: 0;
			}

			&__arrow::before {
				@include arrow-direction('down');
			}
		}

		&[data-popper-placement^='bottom'] .info-tooltip {
			&__arrow-wrapper {
				bottom: 100%;
			}

			&__mouse-guide {
				bottom: 0;
			}

			&__arrow::before {
				@include arrow-direction('up');
			}
		}

		:deep(a) {
			text-decoration: underline;
		}
	}

	&__icon {
		color: $tooltip-color-icon;
	}

	&__primary &__icon {
		color: $tooltip-color-icon-primary;
	}

	&__arrow-wrapper {
		position: absolute;
		z-index: -1;
		width: 26px;
		height: 16px;
		visibility: hidden;
	}

	&__arrow {
		position: absolute;
		inset: 0;
		overflow: hidden;

		&::before {
			content: '';

			position: absolute;
			left: 50%;
			display: block;
			height: 22px;
			width: 22px;
			visibility: visible;
			border: $tooltip-border-width solid $tooltip-color-border;
			background: $tooltip-color-background;
		}
	}

	&__mouse-guide {
		position: absolute;
		bottom: 0;
		left: 50%;
		z-index: 1;
		width: 64px;
		height: 25px;
		transform: translateX(-50%);
		visibility: visible;
	}
}
</style>
